import {
  AccountCreateResponse,
  AddressPlaceResponse,
  AddressSuggestResponse,
  AmlFormResponse,
  AmlSubmitResponse,
  AttributionResponse,
  CompleteResponse,
  CountriesResponse,
  KycSessionRequest,
  KycSessionResponse,
  KycStatusResponse,
  LegalDocsResponse,
  LoginResponse,
  OtpSendResponse,
  OtpVerifyResponse,
  PasswordForgotResponse,
  PasswordResetResponse,
  PartnerResponse,
  PartnerStateResponse,
  ProfilePrefillResponse,
  ProfileSubmitResponse,
  SessionStateResponse,
  TwoFactorResponse,
  type AccountCreateRequest,
  type AmlSubmitRequest,
  type AttributionRequest,
  type CompleteRequest,
  type LoginRequest,
  type OtpResendRequest,
  type OtpSendRequest,
  type OtpVerifyRequest,
  type PasswordForgotRequest,
  type PasswordResetRequest,
  type PartnerLookupRequest,
  type ProfileSubmitRequest,
  type TwoFactorRequest,
} from '@kcm/contracts'
import { api } from './api.js'

/**
 * Every endpoint the funnel calls, in one place.
 *
 * Screens import from here rather than building paths, so a route rename is a
 * single edit and nothing can quietly call an endpoint with the wrong schema.
 */
export const endpoints = {
  attribution: (body: AttributionRequest, signal?: AbortSignal) =>
    api.post('/attribution', AttributionResponse, body, signal),

  countries: (signal?: AbortSignal) => api.get('/meta/countries', CountriesResponse, signal),

  /** Resume state for a returning user. 401 means "start at step 1". */
  session: (signal?: AbortSignal) => api.get('/onboarding/session', SessionStateResponse, signal),

  otp: {
    send: (body: OtpSendRequest, signal?: AbortSignal) =>
      api.post('/auth/otp/send', OtpSendResponse, body, signal),
    /** `escalate: true` is the "I didn't receive it" path — next provider, not a retry. */
    resend: (body: OtpResendRequest, signal?: AbortSignal) =>
      api.post('/auth/otp/resend', OtpSendResponse, body, signal),
    verify: (body: OtpVerifyRequest, signal?: AbortSignal) =>
      api.post('/auth/otp/verify', OtpVerifyResponse, body, signal),
  },

  /**
   * Signing in and getting back in (§10). Neither of these belongs to the
   * funnel: both end at the client cabinet, and `login` may answer with a
   * second factor rather than a URL.
   */
  login: (body: LoginRequest, signal?: AbortSignal) =>
    api.post('/auth/login', LoginResponse, body, signal),
  twoFactor: (body: TwoFactorRequest, signal?: AbortSignal) =>
    api.post('/auth/login/2fa', TwoFactorResponse, body, signal),

  password: {
    /** Answers the same way for every address, account or not (#17). */
    forgot: (body: PasswordForgotRequest, signal?: AbortSignal) =>
      api.post('/auth/password/forgot', PasswordForgotResponse, body, signal),
    reset: (body: PasswordResetRequest, signal?: AbortSignal) =>
      api.post('/auth/password/reset', PasswordResetResponse, body, signal),
  },

  account: (body: AccountCreateRequest, signal?: AbortSignal) =>
    api.post('/onboarding/account', AccountCreateResponse, body, signal),

  /** The partner this application would be credited to (§19.1). */
  partner: {
    current: (signal?: AbortSignal) =>
      api.get('/onboarding/partner', PartnerStateResponse, signal),
    lookup: (body: PartnerLookupRequest, signal?: AbortSignal) =>
      api.post('/onboarding/partner', PartnerResponse, body, signal),
    clear: (signal?: AbortSignal) =>
      api.del('/onboarding/partner', PartnerStateResponse, signal),
  },

  kyc: {
    /** Idempotent upstream — calling it again resumes rather than restarting. */
    start: (body: KycSessionRequest, signal?: AbortSignal) =>
      api.post('/onboarding/kyc/session', KycSessionResponse, body, signal),
    status: (signal?: AbortSignal) =>
      api.get('/onboarding/kyc/status', KycStatusResponse, signal),
  },

  /**
   * The post-KYC confirmation (§11). `get` is Didit's OCR reading, to prefill
   * the form; `submit` is the applicant's confirmed name and address.
   */
  profile: {
    get: (signal?: AbortSignal) => api.get('/onboarding/profile', ProfilePrefillResponse, signal),
    submit: (body: ProfileSubmitRequest, signal?: AbortSignal) =>
      api.post('/onboarding/profile', ProfileSubmitResponse, body, signal),
  },

  /**
   * Address autocomplete for step 2 — a server-side Places proxy, so no
   * Google SDK and no key in this bundle. `session` groups one suggest run
   * with its concluding pick for Google's per-session billing.
   */
  address: {
    suggest: (q: string, session: string, signal?: AbortSignal) =>
      api.get(
        `/onboarding/address/suggest?q=${encodeURIComponent(q)}&session=${encodeURIComponent(session)}`,
        AddressSuggestResponse,
        signal,
      ),
    place: (id: string, session: string, signal?: AbortSignal) =>
      api.get(
        `/onboarding/address/place?id=${encodeURIComponent(id)}&session=${encodeURIComponent(session)}`,
        AddressPlaceResponse,
        signal,
      ),
  },

  /** The AML questionnaire the CRM defines, already normalised (§27.3). */
  amlForm: (signal?: AbortSignal) => api.get('/onboarding/aml-form', AmlFormResponse, signal),

  aml: (body: AmlSubmitRequest, signal?: AbortSignal) =>
    api.post('/onboarding/aml', AmlSubmitResponse, body, signal),

  legalDocs: (signal?: AbortSignal) => api.get('/onboarding/legal-docs', LegalDocsResponse, signal),

  /** Files the acceptances and mints the single-use `direct_login` URL (#2). */
  complete: (body: CompleteRequest, signal?: AbortSignal) =>
    api.post('/onboarding/complete', CompleteResponse, body, signal),
}
